var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer, Stack, Typography, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEventZonedFormat } from "@/hooks/useZonedFormat";
import { ATTR_RESET_VALUE } from "@/types/keycloak";
import { DropFile } from "@sequentech/ui-essentials";
import { ETaskExecutionStatus } from "@sequentech/ui-core";
import { SyncDiffTable } from "./SyncDiffTable";
import { CategorySummary } from "./CategorySummary";
import { DownloadDocument } from "@/resources/User/DownloadDocument";
import { ESyncChangeCategory } from "./types";
import { HIGHLIGHTED_CATEGORIES } from "./constants";
import { formatGeneratedAt } from "./utils";
import ElectionHeader from "@/components/ElectionHeader";
import { DrawerStyles } from "@/components/styles/DrawerStyles";
import { ImportStyles } from "@/components/election-event/import-data/ImportScreen";
import { useWidgetStore } from "@/providers/WidgetsContextProvider";
import { ETasksExecution } from "@/types/tasksExecution";
import { GET_UPLOAD_URL } from "@/queries/GetUploadUrl";
import { CREATE_EXTERNAL_RECONCILIATION_IMPORT } from "@/queries/CreateExternalReconciliationImport";
import { APPLY_EXTERNAL_RECONCILIATION_CHANGES } from "@/queries/ApplyExternalReconciliationChanges";
import { GET_TASK_BY_ID } from "@/queries/GetTaskById";
import { FETCH_DOCUMENT } from "@/queries/FetchDocument";
var POLL_INTERVAL_MS = 3000;
/**
 * Counts distinct voters per category, not rows: a single voter's change can
 * fan out into several `SyncDiffRow`s (e.g. a new voter is 4 rows - area,
 * DoB, channel, enabled - all tagged `VOTER_ADDED`), so counting rows would
 * multiply-count that one voter under every category with more than one
 * field per voter.
 */
var countsByCategory = function (rows) {
    var votersByCategory = new Map();
    rows.forEach(function (row) {
        var _a;
        var voters = (_a = votersByCategory.get(row.category)) !== null && _a !== void 0 ? _a : new Set();
        voters.add(row.voterId);
        votersByCategory.set(row.category, voters);
    });
    var counts = {};
    votersByCategory.forEach(function (voters, category) {
        counts[category] = voters.size;
    });
    return counts;
};
var summarizeForConfirmation = function (rows, t) {
    var counts = countsByCategory(rows);
    var phrases = [
        [
            ESyncChangeCategory.VOTED_OTHER_CHANNEL,
            "reconciliation.wizard.summary.votedOtherChannel",
        ],
        [ESyncChangeCategory.DISABLED_DELETE_CALL, "reconciliation.wizard.summary.disabled"],
        [ESyncChangeCategory.REENABLED, "reconciliation.wizard.summary.reenabled"],
        [ESyncChangeCategory.VOTED_UNMARKED, "reconciliation.wizard.summary.votedUnmarked"],
        [ESyncChangeCategory.PROFILE_UPDATE, "reconciliation.wizard.summary.profileUpdated"],
        [ESyncChangeCategory.VOTER_ADDED, "reconciliation.wizard.summary.voterAdded"],
    ];
    var parts = phrases
        .map(function (_a) {
        var _b;
        var category = _a[0], key = _a[1];
        var count = (_b = counts[category]) !== null && _b !== void 0 ? _b : 0;
        return count > 0 ? t(key, { count: count }) : null;
    })
        .filter(function (part) { return part !== null; });
    return parts.length > 0
        ? t("reconciliation.wizard.summary.prefix", { parts: parts.join(", ") })
        : t("reconciliation.wizard.summary.empty");
};
/** Renders a single old/new element: booleans as "true"/"false" (`Enabled`),
 * otherwise the value as-is (every other field, including each individual
 * `KeycloakUA` attribute, is already a plain string). */
var formatFieldValue = function (value) {
    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }
    return value == null ? ATTR_RESET_VALUE : String(value);
};
/**
 * `KeycloakUA`'s `(old, new)` pair is a bag of Keycloak attributes, not a
 * single field - e.g. "voted via other channel" writes `voted-channel` and
 * `disable-comment` together in one atomic Keycloak edit (see
 * `reconciliation::diff` in windmill), so `new` can carry a key `old` never
 * had. Splitting into one row per attribute key (the union of both bags'
 * keys, each looked up independently) is what keeps that case readable:
 * joining the bags into a single row instead mismatches keys against values
 * whenever the two sides don't share the exact same key set.
 */
var describeFields = function (field, t) {
    var entry = field ? Object.entries(field)[0] : undefined;
    if (!entry) {
        // `null` when the change (e.g. a CountyMun mismatch row failure)
        // has no corresponding field at all - the Reason column explains it.
        return [
            {
                label: t("reconciliation.table.rowLabel"),
                oldValue: ATTR_RESET_VALUE,
                newValue: ATTR_RESET_VALUE,
            },
        ];
    }
    var _a = entry, variantName = _a[0], tuple = _a[1];
    var oldRaw = tuple[0], newRaw = tuple[1];
    if (variantName === "KeycloakUA") {
        var oldBag_1 = (oldRaw !== null && oldRaw !== void 0 ? oldRaw : {});
        var newBag_1 = (newRaw !== null && newRaw !== void 0 ? newRaw : {});
        var keys = Array.from(new Set(__spreadArray(__spreadArray([], Object.keys(oldBag_1), true), Object.keys(newBag_1), true)));
        return keys.map(function (key) {
            var _a, _b;
            return ({
                label: t("usersAndRolesScreen.users.fields.".concat(key), key),
                oldValue: formatFieldValue((_a = oldBag_1[key]) !== null && _a !== void 0 ? _a : null),
                newValue: formatFieldValue((_b = newBag_1[key]) !== null && _b !== void 0 ? _b : null),
            });
        });
    }
    return [
        {
            label: variantName,
            oldValue: formatFieldValue(oldRaw),
            newValue: formatFieldValue(newRaw),
        },
    ];
};
var toRows = function (items, target, t) {
    return items
        .filter(function (item) { return item.target === target && item.category !== ESyncChangeCategory.ROW_FAILURE; })
        .flatMap(function (item, index) {
        var _a;
        return describeFields((_a = item.field) !== null && _a !== void 0 ? _a : null, t).map(function (_a, fieldIndex) {
            var label = _a.label, oldValue = _a.oldValue, newValue = _a.newValue;
            return ({
                id: "".concat(item.voter_username, ":").concat(label, ":").concat(index, ":").concat(fieldIndex),
                voterId: item.voter_username,
                field: label,
                label: label,
                oldValue: oldValue,
                newValue: newValue,
                category: item.category,
                target: item.target,
            });
        });
    });
};
var toRowFailures = function (items, t) {
    return items
        .filter(function (item) { return item.category === ESyncChangeCategory.ROW_FAILURE; })
        .flatMap(function (item, index) {
        var _a;
        return describeFields((_a = item.field) !== null && _a !== void 0 ? _a : null, t).map(function (_a, fieldIndex) {
            var _b;
            var label = _a.label, oldValue = _a.oldValue, newValue = _a.newValue;
            return ({
                id: "failure:".concat(item.voter_username, ":").concat(index, ":").concat(fieldIndex),
                voterId: item.voter_username,
                field: label,
                label: label,
                oldValue: oldValue,
                newValue: newValue,
                category: item.category,
                target: item.target,
                failureReason: (_b = item.failure_reason) !== null && _b !== void 0 ? _b : undefined,
            });
        });
    });
};
/** Converts the backend's version-stable structured task result into the same
 * rows used for generate-time failures. Human task logs remain presentation
 * only and can be reworded without breaking this contract. */
var taskAnnotationsToRowFailures = function (annotations, t) {
    if (!annotations || typeof annotations !== "object") {
        return { rows: [], totalCount: 0, truncated: false };
    }
    var taskResult = annotations;
    var failures = taskResult.reconciliation_row_failures;
    if (!Array.isArray(failures)) {
        return { rows: [], totalCount: 0, truncated: false };
    }
    var rows = failures.flatMap(function (failure, index) {
        if (!failure ||
            typeof failure !== "object" ||
            typeof failure.voter_id !== "string" ||
            typeof failure.reason !== "string") {
            return [];
        }
        var _a = failure, voterId = _a.voter_id, failureReason = _a.reason;
        return [
            {
                id: "apply-failure:".concat(voterId, ":").concat(index),
                voterId: voterId,
                field: t("reconciliation.table.rowLabel"),
                label: t("reconciliation.table.rowLabel"),
                oldValue: ATTR_RESET_VALUE,
                newValue: ATTR_RESET_VALUE,
                category: ESyncChangeCategory.ROW_FAILURE,
                target: "sequent",
                failureReason: failureReason,
            },
        ];
    });
    var annotatedTotal = taskResult.reconciliation_row_failure_count;
    var totalCount = typeof annotatedTotal === "number" &&
        Number.isSafeInteger(annotatedTotal) &&
        annotatedTotal >= rows.length
        ? annotatedTotal
        : rows.length;
    var truncated = taskResult.reconciliation_row_failures_truncated === true || totalCount > rows.length;
    return { rows: rows, totalCount: totalCount, truncated: truncated };
};
/**
 * Reconciliation wizard: Drop the reconciliation file, both diffs (external-side, Sequent-side) are
 * calculated at once and shown in separate tables. The external patch is
 * downloadable as soon as its diff is non-empty; Apply is disabled until that
 * diff is empty (clean import), then applies the Sequent-side diff directly -
 * there is no Sequent patch file to download (the Sequent-side items exist
 * only as an internal document apply_reconciliation_patch reads from).
 * Re-importing an already successfully applied Sequence is an explicit
 * diff-only convergence check: its envelope has `apply_allowed = false` and
 * this wizard never calls the apply route for it.
 *
 * There is no `datafix_reconciliation_import` table: the whole diff is a
 * document referenced from the generate task_execution's own
 * `annotations.document_id`, fetched (via the existing `fetchDocument`
 * action, the same one downloads already use) and parsed client-side once.
 */
export var ReconciliationWizard = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var open = _a.open, electionEventId = _a.electionEventId, onClose = _a.onClose;
    var t = useTranslation().t;
    var zoned = useEventZonedFormat(electionEventId, { seconds: true });
    var _o = useWidgetStore(), addWidget = _o[0], setWidgetTaskId = _o[1];
    var _p = useState("drop"), step = _p[0], setStep = _p[1];
    var _q = useState(null), fileName = _q[0], setFileName = _q[1];
    var _r = useState(null), errorMessage = _r[0], setErrorMessage = _r[1];
    var _s = useState(null), diffDocumentId = _s[0], setDiffDocumentId = _s[1];
    var _t = useState(null), envelope = _t[0], setEnvelope = _t[1];
    var _u = useState(null), generateTaskId = _u[0], setGenerateTaskId = _u[1];
    var _v = useState(null), applyTaskId = _v[0], setApplyTaskId = _v[1];
    var _w = useState(false), confirmOpen = _w[0], setConfirmOpen = _w[1];
    var _x = useState(null), downloadingDocumentId = _x[0], setDownloadingDocumentId = _x[1];
    var getUploadUrl = useMutation(GET_UPLOAD_URL)[0];
    var createImport = useMutation(CREATE_EXTERNAL_RECONCILIATION_IMPORT)[0];
    var applyChanges = useMutation(APPLY_EXTERNAL_RECONCILIATION_CHANGES)[0];
    var generateTaskData = useQuery(GET_TASK_BY_ID, {
        variables: { task_id: generateTaskId },
        skip: !generateTaskId || step !== "processing",
        pollInterval: step === "processing" ? POLL_INTERVAL_MS : 0,
    }).data;
    var applyTaskData = useQuery(GET_TASK_BY_ID, {
        variables: { task_id: applyTaskId },
        skip: !applyTaskId,
        pollInterval: step === "applying" ? POLL_INTERVAL_MS : 0,
    }).data;
    var diffDocumentData = useQuery(FETCH_DOCUMENT, {
        variables: { electionEventId: electionEventId, documentId: diffDocumentId },
        skip: !diffDocumentId || !!envelope,
    }).data;
    // Reads the diff-envelope document's content once its presigned URL is
    // available - a plain client-side fetch+parse, the read-back symmetric
    // counterpart of the raw PUT upload in handleFiles below, since a
    // Document is meant to be fetched by URL, not queried inline.
    useEffect(function () {
        var _a;
        var url = (_a = diffDocumentData === null || diffDocumentData === void 0 ? void 0 : diffDocumentData.fetchDocument) === null || _a === void 0 ? void 0 : _a.url;
        if (!url || envelope) {
            return;
        }
        fetch(url)
            .then(function (response) { return response.json(); })
            .then(function (data) {
            setEnvelope(data);
            setStep("review");
        })
            .catch(function () {
            return setErrorMessage(t("reconciliation.wizard.notifications.envelopeLoadError"));
        });
    }, [(_b = diffDocumentData === null || diffDocumentData === void 0 ? void 0 : diffDocumentData.fetchDocument) === null || _b === void 0 ? void 0 : _b.url, envelope, t]);
    var items = (_c = envelope === null || envelope === void 0 ? void 0 : envelope.items) !== null && _c !== void 0 ? _c : [];
    var datafixRows = useMemo(function () { return toRows(items, "datafix", t); }, [items, t]);
    var sequentRows = useMemo(function () { return toRows(items, "sequent", t); }, [items, t]);
    var rowFailures = useMemo(function () { return toRowFailures(items, t); }, [items, t]);
    var applyTaskAnnotations = (_e = (_d = applyTaskData === null || applyTaskData === void 0 ? void 0 : applyTaskData.sequent_backend_tasks_execution) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.annotations;
    var applyRowFailureSummary = useMemo(function () { return taskAnnotationsToRowFailures(applyTaskAnnotations, t); }, [applyTaskAnnotations, t]);
    var applyRowFailures = applyRowFailureSummary.rows;
    var finalRowFailures = useMemo(function () {
        return Array.from(new Map(__spreadArray(__spreadArray([], rowFailures, true), applyRowFailures, true).map(function (row) {
            var _a;
            return [
                "".concat(row.voterId, ":").concat((_a = row.failureReason) !== null && _a !== void 0 ? _a : ""),
                row,
            ];
        })).values());
    }, [rowFailures, applyRowFailures]);
    var finalRowFailureCount = Math.max(finalRowFailures.length, applyRowFailureSummary.totalCount);
    var finalRowFailuresTruncated = applyRowFailureSummary.truncated && finalRowFailureCount > finalRowFailures.length;
    var isClean = datafixRows.length === 0;
    var summary = useMemo(function () { return countsByCategory(__spreadArray(__spreadArray([], datafixRows, true), sequentRows, true)); }, [datafixRows, sequentRows]);
    var confirmationSummary = useMemo(function () { return summarizeForConfirmation(sequentRows, t); }, [sequentRows, t]);
    // Drives the wizard's own step transitions - separate from the ambient
    // addWidget/setWidgetTaskId call below (which only feeds the app-wide
    // floating task widget, shown everywhere else in the app; it doesn't
    // know how to advance this dialog's own steps).
    var generateStatus = (_g = (_f = generateTaskData === null || generateTaskData === void 0 ? void 0 : generateTaskData.sequent_backend_tasks_execution) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.execution_status;
    var generateDocumentId = (_k = (_j = (_h = generateTaskData === null || generateTaskData === void 0 ? void 0 : generateTaskData.sequent_backend_tasks_execution) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.annotations) === null || _k === void 0 ? void 0 : _k.document_id;
    // Runs as an effect (not inline during render) so it fires once per
    // status/document transition instead of on every render - calling the
    // setters unconditionally during render never stops re-triggering itself
    // since `step` only changes once the envelope effect above finishes,
    // which React reports as "Too many re-renders".
    useEffect(function () {
        if (step !== "processing") {
            return;
        }
        if (generateStatus === ETaskExecutionStatus.SUCCESS && generateDocumentId) {
            setDiffDocumentId(generateDocumentId);
            // Stays on "processing" until the effect above finishes fetching
            // and parsing the envelope document, then moves to "review"
            // itself.
        }
        else if (generateStatus === ETaskExecutionStatus.FAILED) {
            setErrorMessage(t("reconciliation.wizard.notifications.generateFailed"));
            setStep("drop");
        }
    }, [step, generateStatus, generateDocumentId, t]);
    var applyStatus = (_m = (_l = applyTaskData === null || applyTaskData === void 0 ? void 0 : applyTaskData.sequent_backend_tasks_execution) === null || _l === void 0 ? void 0 : _l[0]) === null || _m === void 0 ? void 0 : _m.execution_status;
    useEffect(function () {
        if (step !== "applying") {
            return;
        }
        if (applyStatus === ETaskExecutionStatus.SUCCESS) {
            setStep("done");
        }
        else if (applyStatus === ETaskExecutionStatus.FAILED) {
            setErrorMessage(t("reconciliation.wizard.notifications.applyFailed"));
            setStep("done");
        }
    }, [step, applyStatus, t]);
    var reset = useCallback(function () {
        setStep("drop");
        setFileName(null);
        setErrorMessage(null);
        setDiffDocumentId(null);
        setEnvelope(null);
        setGenerateTaskId(null);
        setApplyTaskId(null);
        setConfirmOpen(false);
        setDownloadingDocumentId(null);
    }, []);
    // The wizard stays mounted across open/close (the parent only flips
    // `open`), so without this a closed-then-reopened wizard would still
    // show whatever step it was left on - the "done" screen from a prior
    // round, or a stale diff from a round closed before applying.
    useEffect(function () {
        if (open) {
            reset();
        }
    }, [open, reset]);
    var handleFiles = function (files) { return __awaiter(void 0, void 0, void 0, function () {
        var file, uploadData, upload, createData, created, widget, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    file = files[0];
                    if (!file) {
                        return [2 /*return*/];
                    }
                    setFileName(file.name);
                    setErrorMessage(null);
                    setStep("processing");
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, getUploadUrl({
                            variables: {
                                name: file.name,
                                media_type: "text/csv",
                                size: file.size,
                                is_public: false,
                                election_event_id: electionEventId,
                            },
                        })];
                case 2:
                    uploadData = (_b.sent()).data;
                    upload = uploadData === null || uploadData === void 0 ? void 0 : uploadData.get_upload_url;
                    if (!(upload === null || upload === void 0 ? void 0 : upload.url) || !(upload === null || upload === void 0 ? void 0 : upload.document_id)) {
                        throw new Error(t("reconciliation.wizard.notifications.uploadUrlError"));
                    }
                    return [4 /*yield*/, fetch(upload.url, {
                            method: "PUT",
                            headers: { "Content-Type": "text/csv" },
                            body: file,
                        })];
                case 3:
                    _b.sent();
                    return [4 /*yield*/, createImport({
                            variables: {
                                election_event_id: electionEventId,
                                document_id: upload.document_id,
                            },
                        })];
                case 4:
                    createData = (_b.sent()).data;
                    created = createData === null || createData === void 0 ? void 0 : createData.create_external_reconciliation_import;
                    if (!((_a = created === null || created === void 0 ? void 0 : created.task_execution) === null || _a === void 0 ? void 0 : _a.id)) {
                        throw new Error(t("reconciliation.wizard.notifications.generateTaskError"));
                    }
                    setGenerateTaskId(created.task_execution.id);
                    widget = addWidget(ETasksExecution.GENERATE_RECONCILIATION_PATCHES, false);
                    setWidgetTaskId(widget.identifier, created.task_execution.id);
                    return [3 /*break*/, 6];
                case 5:
                    error_1 = _b.sent();
                    setErrorMessage(error_1 instanceof Error
                        ? error_1.message
                        : t("reconciliation.wizard.notifications.uploadError"));
                    setStep("drop");
                    return [3 /*break*/, 6];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    var handleApply = function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, taskId, widget, error_2;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!diffDocumentId) {
                        return [2 /*return*/];
                    }
                    setConfirmOpen(false);
                    // A successful equal-Sequence retry is a diff-only convergence check
                    // and must not enqueue apply. A new round with no voter changes still
                    // does enqueue: that records its Sequence and run-level audit log.
                    if (!(envelope === null || envelope === void 0 ? void 0 : envelope.apply_allowed)) {
                        setStep("done");
                        return [2 /*return*/];
                    }
                    setStep("applying");
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, applyChanges({
                            variables: { election_event_id: electionEventId, diff_document_id: diffDocumentId },
                        })];
                case 2:
                    data = (_c.sent()).data;
                    taskId = (_b = (_a = data === null || data === void 0 ? void 0 : data.apply_external_reconciliation_changes) === null || _a === void 0 ? void 0 : _a.task_execution) === null || _b === void 0 ? void 0 : _b.id;
                    if (!taskId) {
                        throw new Error(t("reconciliation.wizard.notifications.applyTaskError"));
                    }
                    setApplyTaskId(taskId);
                    widget = addWidget(ETasksExecution.APPLY_RECONCILIATION_PATCH, false);
                    setWidgetTaskId(widget.identifier, taskId);
                    return [3 /*break*/, 4];
                case 3:
                    error_2 = _c.sent();
                    setErrorMessage(error_2 instanceof Error
                        ? error_2.message
                        : t("reconciliation.wizard.notifications.applyError"));
                    setStep("done");
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(_Fragment, { children: [_jsx(Drawer, { anchor: "right", open: open, onClose: onClose, PaperProps: {
                    sx: {
                        width: { xs: "100vw", sm: "90vw", lg: "80vw" },
                        maxWidth: "1440px",
                    },
                }, children: _jsxs(Box, { sx: { padding: "16px" }, children: [_jsx(ElectionHeader, { title: "reconciliation.wizard.title", subtitle: "reconciliation.wizard.subtitle" }), _jsxs(DrawerStyles.Wrapper, { children: [_jsxs(Stack, { spacing: 3, children: [(step === "drop" || step === "processing") && (_jsxs(_Fragment, { children: [_jsx(Typography, { color: "text.secondary", children: t("reconciliation.wizard.drop.description") }), errorMessage && _jsx(Alert, { severity: "error", children: errorMessage }), step === "drop" ? (_jsx(DropFile, { handleFiles: handleFiles, accept: ".csv", formatLabel: t("reconciliation.wizard.drop.fileFormatLabel") })) : (_jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [_jsx(CircularProgress, { size: 20 }), _jsx(Typography, { children: t("reconciliation.wizard.drop.uploading", {
                                                                fileName: fileName,
                                                            }) })] }))] })), step === "review" && envelope && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        borderRadius: 1,
                                                        p: 1.5,
                                                    }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: t("reconciliation.wizard.review.fileSummary", {
                                                            fileName: fileName,
                                                            sequence: envelope.sequence,
                                                            generatedAt: formatGeneratedAt(envelope.generated_at, zoned.format),
                                                        }) }) }), !envelope.apply_allowed &&
                                                    (datafixRows.length > 0 || sequentRows.length > 0) && (_jsx(Alert, { severity: "warning", children: t("reconciliation.wizard.review.diffOnlyDifferences") })), rowFailures.length > 0 && (_jsxs(Stack, { spacing: 1, children: [_jsx(Alert, { severity: "warning", children: t("reconciliation.wizard.review.rowFailuresWarning", { count: rowFailures.length }) }), _jsx(SyncDiffTable, { rows: rowFailures })] })), datafixRows.length === 0 && sequentRows.length === 0 ? (_jsx(Alert, { severity: "success", children: t("reconciliation.wizard.review.noDifferences") })) : (_jsxs(_Fragment, { children: [_jsx(CategorySummary, { counts: summary }), _jsxs(Stack, { spacing: 1, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [_jsxs(Typography, { variant: "subtitle1", children: [t("reconciliation.wizard.review.externalDiffTitle"), datafixRows.length > 0 &&
                                                                                    " (".concat(datafixRows.length, ")")] }), datafixRows.length > 0 &&
                                                                            envelope.external_patch_document_id && (_jsx(Button, { size: "small", variant: "outlined", onClick: function () {
                                                                                return setDownloadingDocumentId(envelope.external_patch_document_id);
                                                                            }, children: t("reconciliation.wizard.review.downloadExternalPatch") }))] }), datafixRows.length > 0 && (_jsx(Typography, { variant: "caption", color: "text.secondary", children: t("reconciliation.wizard.review.externalDiffCaption") })), _jsx(SyncDiffTable, { rows: datafixRows, emptyMessage: t("reconciliation.wizard.review.noExternalDifferences") })] }), _jsx(Divider, {}), _jsxs(Stack, { spacing: 1, children: [_jsxs(Typography, { variant: "subtitle1", children: [t("reconciliation.wizard.review.sequentDiffTitle"), sequentRows.length > 0 &&
                                                                            " (".concat(sequentRows.length, ")")] }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: t("reconciliation.wizard.review.sequentDiffCaption") }), _jsx(SyncDiffTable, { rows: sequentRows, emptyMessage: t("reconciliation.wizard.review.noSequentDifferences") })] })] }))] })), (step === "applying" || step === "done") && (_jsxs(Stack, { spacing: 2, children: [errorMessage && _jsx(Alert, { severity: "error", children: errorMessage }), step === "applying" && (_jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [_jsx(CircularProgress, { size: 20 }), _jsx(Typography, { children: t("reconciliation.wizard.applying.inProgress") })] })), step === "done" && (_jsx(_Fragment, { children: finalRowFailureCount > 0 ? (_jsxs(Stack, { spacing: 1, children: [_jsx(Alert, { severity: "warning", children: t("reconciliation.wizard.applying.rowFailures", { count: finalRowFailureCount }) }), finalRowFailuresTruncated && (_jsx(Typography, { variant: "caption", color: "text.secondary", children: t("reconciliation.wizard.applying.rowFailuresTruncated", {
                                                                    shown: finalRowFailures.length,
                                                                    count: finalRowFailureCount,
                                                                }) })), _jsx(SyncDiffTable, { rows: finalRowFailures })] })) : !errorMessage ? (_jsx(Alert, { severity: "success", children: t("reconciliation.wizard.applying.success") })) : null }))] })), downloadingDocumentId && (_jsx(DownloadDocument, { documentId: downloadingDocumentId, electionEventId: electionEventId, fileName: "external-reconciliation-".concat(downloadingDocumentId, ".csv"), onDownload: function () { return setDownloadingDocumentId(null); } }))] }), _jsxs(Box, { sx: {
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginTop: "16px",
                                    }, children: [(step === "drop" || step === "processing") && (_jsx(ImportStyles.CancelButton, { onClick: onClose, disabled: step === "processing", children: t("reconciliation.wizard.actions.cancel") })), step === "review" && (_jsxs(_Fragment, { children: [_jsx(ImportStyles.CancelButton, { onClick: reset, children: t("reconciliation.wizard.actions.back") }), _jsx(ImportStyles.ImportButton, { disabled: !isClean ||
                                                        !envelope ||
                                                        (!envelope.apply_allowed && sequentRows.length > 0), onClick: function () {
                                                        return sequentRows.length > 0
                                                            ? setConfirmOpen(true)
                                                            : handleApply();
                                                    }, children: sequentRows.length > 0
                                                        ? t("reconciliation.wizard.actions.apply")
                                                        : t("reconciliation.wizard.actions.next") })] })), step === "applying" && (_jsx(ImportStyles.CancelButton, { disabled: true, children: t("reconciliation.wizard.actions.back") })), step === "done" && (_jsxs(_Fragment, { children: [_jsx(ImportStyles.CancelButton, { onClick: reset, children: t("reconciliation.wizard.actions.startOver") }), _jsx(ImportStyles.ImportButton, { onClick: onClose, children: t("reconciliation.wizard.actions.close") })] }))] })] })] }) }), _jsxs(Dialog, { open: confirmOpen, onClose: function () { return setConfirmOpen(false); }, children: [_jsx(DialogTitle, { children: t("reconciliation.wizard.confirm.title") }), _jsx(DialogContent, { children: _jsxs(Stack, { spacing: 2, sx: { pt: 1 }, children: [_jsx(Typography, { children: confirmationSummary }), _jsx(CategorySummary, { counts: countsByCategory(sequentRows), highlighted: HIGHLIGHTED_CATEGORIES }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: t("reconciliation.wizard.confirm.categoriesNote", {
                                        categories: [
                                            ESyncChangeCategory.VOTED_OTHER_CHANNEL,
                                            ESyncChangeCategory.DISABLED_DELETE_CALL,
                                        ]
                                            .map(function (category) {
                                            return t("reconciliation.categories.".concat(category), category);
                                        })
                                            .join(", "),
                                    }) })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: function () { return setConfirmOpen(false); }, children: t("reconciliation.wizard.actions.cancel") }), _jsx(Button, { variant: "contained", onClick: handleApply, children: sequentRows.length > 0
                                    ? t("reconciliation.wizard.confirm.applyChanges")
                                    : t("reconciliation.wizard.confirm.continue") })] })] })] }));
};
export default ReconciliationWizard;
